<template>
  <div class="aud">
    <div class="aud-header">
      <h5 class="aud-title">Auditoria</h5>
      <p class="aud-hint">Trilha cronológica de alterações neste gatilho.</p>
    </div>

    <div v-if="loading" class="aud-loading">
      <fa-icon icon="spinner" spin /> Carregando auditoria...
    </div>

    <div v-else-if="!items.length" class="aud-empty">
      <fa-icon icon="history" class="aud-empty__icon" />
      <p class="aud-empty__title">Sem registros de auditoria</p>
      <p class="aud-empty__hint">Alterações futuras ficarão registradas aqui.</p>
    </div>

    <div v-else class="aud-list">
      <div v-for="(h, idx) in items" :key="h.id" class="aud-item">
        <div class="aud-item__rail">
          <div class="aud-item__dot" :class="dotClass(h.acao)"></div>
          <div v-if="idx < items.length - 1" class="aud-item__line"></div>
        </div>
        <div class="aud-item__body">
          <div class="aud-item__meta">
            <span class="aud-item__acao" :class="`aud-item__acao--${h.acao}`">{{ acaoLabel(h.acao) }}</span>
            <span class="aud-item__date"><fa-icon icon="clock" /> {{ formatarData(h.created_at) }}</span>
            <span class="aud-item__author"><fa-icon icon="user" /> {{ h.changed_by_nome || h.changed_by || '—' }}</span>
          </div>
          <pre v-if="h.diff" class="aud-item__diff">{{ formatDiff(h.diff) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { get } from '@/helpers/apiRequest';

const ACAO_LABELS = {
  criado: 'Criado',
  atualizado: 'Atualizado',
  ativado: 'Ativado',
  desativado: 'Desativado',
  duplicado: 'Duplicado',
  excluido: 'Excluído',
};

export default {
  name: 'AcaoObjetoAuditoriaTab',
  props: {
    endpointBase: { type: String, required: true },
    acaoId: { type: [Number, String], default: null },
  },
  data() {
    return { items: [], loading: false };
  },
  watch: {
    acaoId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    load() {
      if (!this.acaoId) { this.items = []; return; }
      this.loading = true;
      get(`${this.endpointBase}/${this.acaoId}/auditoria`)
        .then(json => { this.items = json.data || json.itens || json; this.loading = false; })
        .catch(() => { this.loading = false; });
    },
    acaoLabel(a) { return ACAO_LABELS[a] || a; },
    dotClass(a) { return `is-${a}`; },
    formatarData(s) {
      if (!s) return '—';
      try { return new Date(s).toLocaleString('pt-BR'); } catch (_) { return s; }
    },
    formatDiff(d) { try { return JSON.stringify(d, null, 2); } catch (_) { return String(d); } },
  },
};
</script>

<style scoped>
.aud-header { margin-bottom: 14px; }
.aud-title { margin: 0; font-size: 13px; font-weight: 700; color: #1d2939; text-transform: uppercase; letter-spacing: 0.4px; }
.aud-hint { margin: 2px 0 0; font-size: 12px; color: #667085; }

.aud-loading, .aud-empty { text-align: center; padding: 32px; color: #98a2b3; }
.aud-empty__icon { font-size: 30px; color: #d0d5dd; margin-bottom: 10px; opacity: 0.6; }
.aud-empty__title { font-size: 13px; font-weight: 600; color: #475467; margin: 6px 0 2px; }
.aud-empty__hint { font-size: 12px; color: #98a2b3; margin: 0; }

.aud-list { padding-left: 4px; }
.aud-item { display: flex; gap: 14px; }
.aud-item__rail { position: relative; width: 14px; flex-shrink: 0; display: flex; flex-direction: column; align-items: center; padding-top: 8px; }
.aud-item__dot { width: 12px; height: 12px; border-radius: 50%; background: #d0d5dd; border: 2px solid #fff; box-shadow: 0 0 0 2px #d0d5dd; z-index: 1; }
.aud-item__dot.is-criado    { background: #3a3e81; box-shadow: 0 0 0 2px #3a3e81; }
.aud-item__dot.is-atualizado { background: #2298a3; box-shadow: 0 0 0 2px #2298a3; }
.aud-item__dot.is-ativado    { background: #5eb95e; box-shadow: 0 0 0 2px #5eb95e; }
.aud-item__dot.is-desativado { background: #f0ad4e; box-shadow: 0 0 0 2px #f0ad4e; }
.aud-item__dot.is-duplicado  { background: #8e44ad; box-shadow: 0 0 0 2px #8e44ad; }
.aud-item__dot.is-excluido   { background: #d9534f; box-shadow: 0 0 0 2px #d9534f; }
.aud-item__line { flex: 1; width: 2px; background: #e4e7eb; margin-top: 4px; }

.aud-item__body { flex: 1; background: #fff; border: 1px solid #e4e7eb; border-radius: 8px; padding: 10px 12px; margin-bottom: 12px; }
.aud-item__meta { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; font-size: 11px; color: #667085; }
.aud-item__acao { padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
.aud-item__acao--criado    { background: #f2f2f8; color: #3a3e81; }
.aud-item__acao--atualizado { background: #edf8f9; color: #2298a3; }
.aud-item__acao--ativado    { background: #ecfaee; color: #2e7d32; }
.aud-item__acao--desativado { background: #fef6e6; color: #b85f00; }
.aud-item__acao--duplicado  { background: #f4ecf7; color: #6c3483; }
.aud-item__acao--excluido   { background: #fdecea; color: #b3261e; }
.aud-item__date, .aud-item__author { display: inline-flex; align-items: center; gap: 4px; }
.aud-item__diff {
  background: #fafbfc;
  border: 1px solid #f2f4f7;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 8px 0 0;
  max-height: 160px;
  overflow: auto;
  font-family: 'Menlo', 'Monaco', 'Consolas', monospace;
  font-size: 11px;
  color: #1d2939;
  white-space: pre-wrap;
}
</style>
